import { forwardRef, type SelectHTMLAttributes } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import './control.css';
import './Select.css';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  options: SelectOption[];
  /** Optional placeholder rendered as a disabled first option. */
  placeholder?: string;
  invalid?: boolean;
  size?: 'sm' | 'md';
}

/**
 * Native `<select>` styled to the form-control vocabulary. Native is the right
 * call here: it is accessible, keyboard-complete, and renders the OS picker on
 * touch. A chevron is layered on top; the arrow is hidden via appearance reset.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { options, placeholder, invalid, size = 'md', className, value, defaultValue, 'aria-invalid': ariaInvalid, ...rest },
  ref,
) {
  const isInvalid = invalid || ariaInvalid === true || ariaInvalid === 'true';
  const showPlaceholder = placeholder && value === undefined && defaultValue === undefined;

  return (
    <div className={cn('select-wrap', className)}>
      <select
        ref={ref}
        className={cn('control', `control--${size}`, 'select')}
        aria-invalid={isInvalid || undefined}
        value={value}
        defaultValue={showPlaceholder ? '' : defaultValue}
        {...rest}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value} disabled={opt.disabled}>
            {opt.label}
          </option>
        ))}
      </select>
      <ChevronDown className="select-wrap__chevron" aria-hidden="true" />
    </div>
  );
});
